import { CampaignForm } from '@/components/compliance';
import { ComplianceLayout, useComplianceLayout } from '@/components/compliance/compliance-layout';
import { useSubmitCampaign } from '@/components/compliance/hooks/use-submit-campaign';
import { useUpdateCampaignDraft } from '@/components/compliance/hooks/use-update-campaign-draft';
import { StepIntro } from '@/components/compliance/step-intro';
import type { CampaignSubmitPayload } from '@/components/compliance/types';
import { Modal } from '@/components/ui/design-system';
import { ANALYTICS_EVENTS, analytics } from '@/services/analytics';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

const CampaignRouteContent = () => {
  const navigate = useNavigate();
  const { brand } = useComplianceLayout();
  const draftCampaign = brand?.campaigns?.[0];
  const campaignId = draftCampaign?.id as string | undefined;

  const { updateAsync: updateDraftAsync } = useUpdateCampaignDraft(campaignId);

  const {
    submit: submitCampaign,
    isSubmitting,
    error: submitError,
    retryAfter,
  } = useSubmitCampaign({
    onSuccess: () => navigate({ to: '/settings/compliance' }),
  });

  const [isReviewing, setIsReviewing] = useState(false);

  // The submit mutation drives `isSubmitting`; once it flips back to false we
  // know the AI review request has resolved (success, error, or rate limit) and
  // can dismiss the reviewing dialog.
  useEffect(() => {
    if (!isSubmitting && isReviewing) {
      setIsReviewing(false);
    }
  }, [isSubmitting, isReviewing]);

  const handleSubmit = async (
    data: CampaignSubmitPayload,
    options: { skipReview?: boolean } = {},
  ) => {
    if (!campaignId) {
      toast.error(
        "We couldn't find a draft campaign for your brand. Please refresh and try again.",
      );
      return;
    }
    try {
      await updateDraftAsync(data);
    } catch {
      return;
    }
    if (!options.skipReview) {
      setIsReviewing(true);
    }
    submitCampaign(campaignId, { skipReview: options.skipReview });
  };

  if (!brand) {
    return (
      <p className="text-body-13 text-primary-60">
        You need to register a brand before creating a campaign.
      </p>
    );
  }

  return (
    <>
      <StepIntro
        step={2}
        title="Create your campaign"
        subtitle="Describe the kind of messages you'll send and share a few examples. The AI review can catch common issues before you submit."
      />
      <CampaignForm
        brandName={brand.display_name || brand.company_name || 'Your Brand'}
        brandEmail={brand.email ?? ''}
        brandEntityType={brand.entity_type}
        campaignId={campaignId}
        initialCampaign={draftCampaign}
        onSubmit={handleSubmit}
        onCancel={() => navigate({ to: '/settings/compliance/brand' })}
        isLoading={isSubmitting}
        error={submitError}
        retryAfter={retryAfter}
      />
      <Modal
        isOpen={isReviewing}
        title="Reviewing your campaign"
        icon={() => <Sparkles className="size-4 text-status-success" />}
        onClose={() => {
          /* dialog dismisses itself when the review completes */
        }}
        closeOnBackdrop={false}
        hideCloseButton
      >
        <div className="flex flex-col items-center gap-4 py-6">
          <div className="relative flex size-14 items-center justify-center">
            <span className="absolute inset-0 animate-ping rounded-full bg-status-success/20" />
            <span className="absolute inset-1 animate-pulse rounded-full bg-status-success/15" />
            <Sparkles className="relative size-6 animate-pulse text-status-success" />
          </div>
          <p className="text-center text-body-13 text-primary-80">
            Our <strong>10DLC AI Reviewer</strong> is checking your campaign details against carrier
            guidelines. This can take a while, please don't close this window.
          </p>
        </div>
      </Modal>
    </>
  );
};

const RouteComponent = () => {
  return (
    <ComplianceLayout currentStep={2}>
      <CampaignRouteContent />
    </ComplianceLayout>
  );
};

export const Route = createFileRoute('/_app/settings/compliance/campaign')({
  component: RouteComponent,
  onEnter: () => analytics.track(ANALYTICS_EVENTS.COMPLIANCE.CAMPAIGN_CREATION_STARTED),
});
